'use client';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDeleteForever } from 'react-icons/md';
import axios from 'axios';
import { config } from '../../Constants';
import toast from 'react-hot-toast';
import { SpinnerPurple } from '../spinner/Spinner';
import secureLocalStorage from 'react-secure-storage';
import Searchbar from '../search/Searchbar';
import { formatCurrency } from '../../utils/format';
import { FaEye } from 'react-icons/fa';

export const MessagesTable = ({ data }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [currentMsg, setCurrentMsg] = useState({});
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const [searchQuery, setSearchQuery] = useState('');

  const deleteMessage = async () => {
    try {
      setLoading(true);
      const { data } = await axios.delete(
        `${baseURL}/contact-us/${currentMsg._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      toast.success(data.msg);
      close();
      navigate(0);
    } catch (error) {
      setLoading(false);
      toast.success(error.response.data.msg);
    }
  };

  const filteredData = data.filter((info) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      info.name.toLowerCase().includes(searchValue) ||
      info.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search messages...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='messages-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>EMAIL</TableCell>
                <TableCell align='right'>SENT</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.map((info) => {
                const { createdAt, email, name, _id: id } = info;
                return (
                  <TableRow
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                    style={{ cursor: `pointer` }}
                    key={id}
                    className='user-row'
                    onClick={() => {
                      setCurrentMsg(data.filter((msg) => msg._id === id)[0]);
                      open();
                    }}
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {name}
                    </TableCell>
                    <TableCell align='right'>{email}</TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <Modal
        opened={opened}
        onClose={close}
        centered
        size='lg'
        className='message-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='title'>
          <h2>
            Message sent by <span>{currentMsg.name}</span>
          </h2>
          <h3>{currentMsg.email}</h3>
        </div>

        <div className='info'>
          <p>{currentMsg.message}</p>
        </div>

        <div className='del-btn'>
          {loading ? (
            <SpinnerPurple />
          ) : (
            <MdDeleteForever onClick={deleteMessage} />
          )}
        </div>
      </Modal>
    </>
  );
};

export const UsersTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const filteredData = data.filter((user) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      user.fullName.toLowerCase().includes(searchValue) ||
      user.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search users...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='users-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>EMAIL</TableCell>
                <TableCell align='right'>PHONE</TableCell>
                <TableCell align='right'>SIGNED UP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((user) => {
                const { _id, createdAt, email, fullName, phone } = user;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='user-row'
                    onClick={() => navigate(`/dashboard/users/${_id}`)}
                  >
                    <TableCell component='th' scope='row'>
                      {fullName}
                    </TableCell>
                    <TableCell align='right'>{email}</TableCell>
                    <TableCell align='right'>{phone ?? `N/A`}</TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const DepositsTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredData = data.filter((withdrawal) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      withdrawal.userName.toLowerCase().includes(searchValue) ||
      withdrawal.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search deposits...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='deposits-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>DEPOSIT AMOUNT</TableCell>
                <TableCell align='right'>TIME</TableCell>
                <TableCell align='right'>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((deposit) => {
                const { _id, createdAt, userName, status, amount } = deposit;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='deposit-row'
                    onClick={() => navigate(`/dashboard/deposits/${_id}`)}
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right'>${amount}</TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                    <TableCell align='right' className='status'>
                      <span className={status}>{status}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const BankWithdrawalsTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredData = data.filter((withdrawal) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      withdrawal.userName.toLowerCase().includes(searchValue) ||
      withdrawal.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search withdrawals...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <TableContainer component={Paper} className='withdrawal-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>WITHDRAWAL AMOUNT</TableCell>
                <TableCell align='right'>TIME</TableCell>
                <TableCell align='right'>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((withdrawal) => {
                const { _id, createdAt, userName, status, amount } = withdrawal;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='withdrawal-row'
                    onClick={() =>
                      navigate(`/dashboard/bank-withdrawals/${_id}`)
                    }
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right'>${amount}</TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                    <TableCell align='right' className='status'>
                      <span className={status}>{status}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const CryptoWithdrawalsTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredData = data.filter((withdrawal) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      withdrawal.userName.toLowerCase().includes(searchValue) ||
      withdrawal.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search withdrawals...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <TableContainer component={Paper} className='withdrawal-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>WITHDRAWAL AMOUNT</TableCell>
                <TableCell align='right'>TIME</TableCell>
                <TableCell align='right'>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((withdrawal) => {
                const { _id, createdAt, userName, status, amount } = withdrawal;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='withdrawal-row'
                    onClick={() =>
                      navigate(`/dashboard/crypto-withdrawals/${_id}`)
                    }
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right'>${amount}</TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                    <TableCell align='right' className='status'>
                      <span className={status}>{status}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const ProfitsTable = ({ data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredData = data.filter((info) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      info.userName.toLowerCase().includes(searchValue) ||
      info.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search profits...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='profits-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>INVESTMENT PLAN</TableCell>
                <TableCell align='right'>PROFIT AMOUNT</TableCell>
                <TableCell align='right'>TIME SENT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((profit) => {
                const { _id, createdAt, userName, amount, plan } = profit;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='profit-row'
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right' className='amount'>
                      {plan}
                    </TableCell>
                    <TableCell align='right' className='amount'>
                      {formatCurrency(amount)}
                    </TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const BonusTable = ({ data }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [currentNarration, setCurrentNarration] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const filteredData = data.filter((info) => {
    const searchValue = searchQuery.toLowerCase();
    return (
      info.userName.toLowerCase().includes(searchValue) ||
      info.email.toLowerCase().includes(searchValue)
    );
  });

  return (
    <>
      <Searchbar
        placeholder='Search bonus...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='bonus-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>BONUS AMOUNT</TableCell>
                <TableCell align='right'>BONUS TYPE</TableCell>
                <TableCell align='right'>BONUS DESCRIPTION</TableCell>
                <TableCell align='right'>TIME SENT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((profit) => {
                const { _id, createdAt, userName, amount, type, narration } =
                  profit;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    key={_id}
                    className='bonus-row'
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right'>
                      {formatCurrency(amount)}
                    </TableCell>
                    <TableCell align='right'>{type}</TableCell>
                    <TableCell align='center' className='narration'>
                      <FaEye
                        onClick={() => {
                          setCurrentNarration(narration);
                          open();
                        }}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <Modal
        opened={opened}
        onClose={close}
        centered
        size='lg'
        className='message-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='title'>
          <h2>Narration</h2>
        </div>

        <div className='info'>
          <p>{currentNarration}</p>
        </div>
      </Modal>
    </>
  );
};

export const TradingPlansTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  console.log(data);

  const filteredData = data.filter((plan) => {
    console.log(plan);

    const searchValue = searchQuery.toLowerCase();
    return plan.name.toLowerCase().includes(searchValue);
  });

  return (
    <>
      <Searchbar
        placeholder='Search trading plans...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='trading-plans-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>DURATION</TableCell>
                <TableCell align='right'>MIN DEPOSIT</TableCell>
                <TableCell align='right'>MAX DEPOSIT</TableCell>
                <TableCell align='right'>ROI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((plan) => {
                const { duration, maxDeposit, minDeposit, name, roi, _id } =
                  plan;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='deposit-row'
                    onClick={() => navigate(`/dashboard/trading-plans/${_id}`)}
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {name}
                    </TableCell>
                    <TableCell component='th' scope='row' align='right'>
                      {duration}
                    </TableCell>
                    <TableCell component='th' scope='row' align='right'>
                      ${minDeposit.toLocaleString(`en-US`)}
                    </TableCell>
                    <TableCell component='th' scope='row' align='right'>
                      ${maxDeposit.toLocaleString(`en-US`)}
                    </TableCell>
                    <TableCell align='right'>{roi}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export const KYCsTable = ({ data }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredData = data.filter((document) => {
    const searchValue = searchQuery.toLowerCase();
    return document.userName.toLowerCase().includes(searchValue);
  });

  return (
    <>
      <Searchbar
        placeholder='Search users...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <TableContainer component={Paper} className='kycs-table'>
        {filteredData.length === 0 ? (
          <div className='no-search-match'>
            <p>Nothing matches your search</p>
          </div>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>NAME</TableCell>
                <TableCell align='right'>DOCUMENT NAME</TableCell>
                <TableCell align='right'>TIME SENT</TableCell>
                <TableCell align='right'>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((kyc) => {
                const { _id, createdAt, userName, status, document } = kyc;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    style={{ cursor: `pointer` }}
                    key={_id}
                    className='kyc-row'
                    onClick={() => navigate(`/dashboard/kyc/${_id}`)}
                  >
                    <TableCell component='th' scope='row' className='name'>
                      {userName}
                    </TableCell>
                    <TableCell align='right' className='document'>
                      {document}
                    </TableCell>
                    <TableCell align='right'>
                      {moment(createdAt).format(`LLL`)}
                    </TableCell>
                    <TableCell align='right' className='status'>
                      <span className={status}>{status}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};
